<script>
  import moment from 'moment';
  import { onMount, createEventDispatcher } from 'svelte';
  import { navigate } from 'svelte-routing';
  import { apiGet, apiUpdate, apiCreate, notify, apiCall } from '../httpCommon';
  import Paper, { Title, Content as PaperContent } from '@smui/paper';
  import Card, { Actions, ActionIcons } from '@smui/card';
  import IconButton from '@smui/icon-button';
  import Button, { Label, Icon } from '@smui/button';

  import Switch from '@smui/switch';
  import Checkbox from '@smui/checkbox';
  import FormField from '@smui/form-field';
  import { Textfield, Textshow, RichEdit, ResourceShow } from '../components';
  import ResourceSelect from '../components/ResourceSelect.svelte';
  import Address from '../address/AddressEdit.svelte';
  import Contacts from '../users/Contacts.svelte';
  import MenuItems from './menu-items/MenuItems.svelte';
  import { PopupList } from '../popups';

  export let id;
  export let title;

  const resource = 'vendors';
  const basePath = `/${resource}`;
  const path = `/${resource}/${id}`;
  const defaultData = { vat_registered: false };
  let data = null;

  let summary;

  let errors = {};

  $: isCreate = id === 'create';

  export let edit = false;

  $: {
    if (isCreate) {
      title = 'Create Vendor';
    } else if (data && data.vendor_name) {
      title = data.vendor_name;
    } else {
      title = '';
    }
  }

  $: edit = edit || (errors && Object.keys(errors).length);

  function toggleEdit() {
    edit = !edit;
    console.log('edit: ', edit);
  }

  async function load() {
    try {
      summary = await apiGet(basePath, id + '/summary');
    } catch (error) {
      notify(error);
    }
  }

  function saved(e) {
    edit = false;
  }

  onMount(() => {
    if (isCreate) {
      data = defaultData;
    } else {
      load();
    }
  });

  let image = { menu: {}, logo: {} };

  function setImage(e, typ) {
    console.log('setImage: ', image[typ]);
    image[typ].error = null;
    image[typ].file = null;

    if (e.target.files && e.target.files.length) {
      let f = e.target.files[0];
      if (f.type != 'image/png' && f.type != 'image/jpeg' && f.type != 'image/webp') {
        image[typ].error = 'bad file type, try .png or .jpeg)';
      } else {
        image[typ].file = f;
        saveImage(typ);
      }
    }
  }

  async function saveImage(typ) {
    if (image[typ].file) {
      const formData = new FormData();
      formData.append('image', image[typ].file);
      const response = await apiCall(`${path}/image/${typ}`, {
        method: 'POST',
        body: formData,
        headers: {},
      });
      image[typ] = {};
      notify({ message: 'Image uploaded' });
      data = await response.json();
      image[typ].file_name = '';
    }
  }
</script>

<ResourceShow {resource} bind:data bind:errors {id} on:updated={saved} on:created={saved}>
  <div slot="title" let:save let:load let:close let:loading>
    <Title class="flex">
      <span />
      <span>
        {#if isCreate}
          <Button on:click={save} disabled={loading}>
            <Icon class="material-icons">save</Icon>
            <Label>save</Label>
          </Button>
          <Button on:click={close} disabled={loading}>
            <Icon class="material-icons">cancel</Icon>
            <Label>cancel</Label>
          </Button>
        {:else if edit}
          <Button on:click={load} disabled={loading}>
            <Icon class="material-icons">replay</Icon>
            <Label>Reload</Label>
          </Button>
          <Button on:click={save} disabled={loading}>
            <Icon class="material-icons">save</Icon>
            <Label>save</Label>
          </Button>
          <Button on:click={() => (edit = false)} disabled={loading}>
            <Icon class="material-icons">cancel</Icon>
            <Label>cancel</Label>
          </Button>
        {:else}
          <Button on:click={() => (edit = true)} disabled={loading}>
            <Icon class="material-icons">settings_applications</Icon>
            <Label>Settings</Label>
          </Button>
        {/if}
      </span>
    </Title>
  </div>
  <div slot="content" class="flex wrap">
    {#if data}
      {#if edit || isCreate}
        <div class="card">
          <Card variant="outlined" padded>
            <h3>Info</h3>
            <Textfield class="full-width" bind:value={data.vendor_name} error={errors.vendor_name} label="Vendor Name" />
            <Textfield bind:value={data.registered_name} error={errors.registered_name} label="Registered Name" />
            <Textfield bind:value={data.fd_email_address} error={errors.fd_email_address} label="FD email address" />
            <Textfield bind:value={data.company_number} error={errors.company_number} label="Company Number" />
            <Textfield input$maxlength={2} bind:value={data.invoice_prefix} error={errors.invoice_prefix} label="Prefix for Catering Invoices" />
            <Textfield type="number" bind:value={data.commission_rate_pct} error={errors.commission_rate_pct} label="Commission %" />
            <Textfield textarea bind:value={data.notes} error={errors.notes} label="Food disco notes" />
            <div class="switch">
              <div>
                <Textfield bind:value={data.vat_number} error={errors.vat_number} disabled={!data.vat_registered} label="VAT number" />
              </div>
              <FormField>
                <Switch bind:checked={data.vat_registered} />
                <span slot="label">VAT Registered</span>
              </FormField>
            </div>
            <div class="switch">
              <Textfield bind:value={data.square_location_id} error={errors.square_location_id} label="Square Location ref" />
              <FormField>
                <Checkbox on:change={(e) => (data.update_square = e.target.checked)} disabled={!data.square_location_id} />
                <span slot="label">Update Square</span>
              </FormField>
            </div>
          </Card>
        </div>
        <div class="card">
          <Address bind:data={data.address} errors={errors.address} />
        </div>
        <div class="card extra-wide">
          <Card variant="outlined" padded>
            <h3 style="margin: 10px 0">Menu Info</h3>
            <span style="display: inline-block; margin: 0 0 10px 0;">Note: create a new paragraph by starting a new line.</span>
            <RichEdit bind:value={data.menu_blurb} error={errors.menu_blurb} />
            <div class="flex no-spread vcentre">
              <Textfield type="file" helperText="Menu Image" helperPersistent={true} value={image.menu.file_name} error={image.menu.error} on:change={(e) => setImage(e, 'menu')} />
              {#if image.menu.file}
                <Button on:click={(e) => saveImage('menu')} disabled={!image.menu.file}>
                  <Label>Save Image</Label>
                </Button>
              {/if}
            </div>
            <div class="flex no-spread">
              <Textfield type="file" helperText="Logo Image" helperPersistent={true} value={image.logo.file_name} error={image.logo.error} on:change={(e) => setImage(e, 'logo')} />
              {#if image.logo.file}
                <Button on:click={(e) => saveImage('logo')} disabled={!image.logo.file}>
                  <Label>Save Image</Label>
                </Button>
              {/if}
            </div>
            {#if data.menu_image}
              <div class="vendor-image" style="background: linear-gradient(0deg, rgba(61, 59, 59, 0.15),
                rgba(61, 59, 59, 0.15)), url({data.menu_image}) left/cover no-repeat">
                {#if data.logo_image}
                  <div class="vendor-logo">
                    <div style="background: url({data.logo_image}) center/contain no-repeat" />
                  </div>
                {/if}
              </div>
            {/if}
          </Card>
        </div>
      {:else if !isCreate}
        {#if summary}
          <div class="card">
            <Card variant="outlined" padded>
              <h3>Summary</h3>
              <table class="summary">
                <tr>
                  <th>Popups:</th>
                  <td>{summary.popups.num_events}</td>
                  <th>Catered:</th>
                  <td>{summary.catered.num_events}</td>
                </tr>
                <tr>
                  <th>Total Takings:</th>
                  <td>{summary.popups.total_takings}</td>
                  <th>Total Value:</th>
                  <td>{summary.catered.total_value}</td>
                </tr>
                <tr>
                  <th>Avg Takings:</th>
                  <td>{summary.popups.average_takings}</td>
                  <th>Avg Value:</th>
                  <td>{summary.catered.average_value}</td>
                </tr>
                <tr>
                  <th>Total Sales:</th>
                  <td>{summary.popups.total_items}</td>
                </tr>
                <tr>
                  <th>Avg Sales:</th>
                  <td>{summary.popups.average_items}</td>
                </tr>
              </table>
            </Card>
          </div>
        {/if}

        {#if !isCreate}
          <div class="card wide">
            <Contacts {path} />
          </div>
          <div class="card wide">
            <MenuItems vendorId={id} title="Menu Items" topLevel={false} groupOptions={data.menu_item_groups} flagOptions={data.menu_item_flags} />
          </div>
        {/if}

        <div class="card wide">
          <PopupList vendorId={id} title="Popups" topLevel={false} />
        </div>
      {/if}
    {/if}
  </div>
</ResourceShow>

<style>
  .switch {
    display: flex;
  }

  table.summary th {
    text-align: right;
  }
  .vendor-image {
    width: 922px;
    height: 240px;
    margin-top: 20px;
    margin-bottom: 60px;
  }
  .vendor-logo {
    position: absolute;
    margin-top: 170px;
    margin-left: 571px;
    width: 120px;
    height: 120px;
    border-radius: 60px;
    box-shadow: 1px 1px 2px gray, 0px 1px 2px gray, -1px 1px 2px gray;
    background-color: white;
    overflow: hidden;
  }
  .vendor-logo div {
    width: 120px;
    height: 120px;
  }
</style>
